import React from 'react';

interface CatAvatarProps {
  color?: string;
  expression?: string;
  accessory?: string;
  className?: string;
  size?: number;
}

export default function CatAvatar({
  color = 'orange',
  expression = 'happy',
  accessory = 'none',
  className = '',
  size = 120
}: CatAvatarProps) {
  // Color hex mappings
  const colorMap: Record<string, { body: string; shadow: string; ears: string }> = {
    orange: { body: '#F97316', shadow: '#EA580C', ears: '#FED7AA' }, // orange-500, orange-600, orange-200
    grey: { body: '#94A3B8', shadow: '#64748B', ears: '#E2E8F0' },   // slate-400, slate-500, slate-200
    white: { body: '#F8FAFC', shadow: '#CBD5E1', ears: '#F1F5F9' },  // slate-50, slate-300, slate-100
    black: { body: '#334155', shadow: '#1E293B', ears: '#FDA4AF' },  // slate-700, slate-800, pink-300
    gold: { body: '#F59E0B', shadow: '#D97706', ears: '#FEF3C7' }    // amber-500, amber-600, amber-100
  };

  const selectedColor = colorMap[color] || colorMap.orange;

  return (
    <div 
      className={`relative flex items-center justify-center select-none ${className}`} 
      style={{ width: size, height: size }}
    >
      <svg
        viewBox="0 0 200 200"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="w-full h-full drop-shadow-md"
      >
        {/* Shadow base inside SVG */}
        <ellipse cx="100" cy="180" rx="60" ry="12" fill="#000000" fillOpacity="0.1" />

        {/* Tail */}
        <path
          d="M 145 155 Q 180 140 170 100 Q 160 70 175 60 Q 185 50 180 40 Q 170 35 160 55 Q 145 80 152 110 Q 155 125 140 145"
          fill={selectedColor.body}
          stroke={selectedColor.shadow}
          strokeWidth="3"
          strokeLinecap="round"
        />

        {/* Left Ear */}
        <path
          d="M 40 85 L 25 25 Q 22 20 28 23 L 78 57 Z"
          fill={selectedColor.body}
          stroke={selectedColor.shadow}
          strokeWidth="4"
          strokeLinejoin="round"
        />
        <path
          d="M 45 77 L 34 35 Q 32 33 35 34 L 70 58 Z"
          fill={selectedColor.ears}
        />

        {/* Right Ear */}
        <path
          d="M 160 85 L 175 25 Q 178 20 172 23 L 122 57 Z"
          fill={selectedColor.body}
          stroke={selectedColor.shadow}
          strokeWidth="4"
          strokeLinejoin="round"
        />
        <path
          d="M 155 77 L 166 35 Q 168 33 165 34 L 130 58 Z"
          fill={selectedColor.ears}
        />

        {/* Main Body/Face */}
        <circle cx="100" cy="115" r="72" fill={selectedColor.body} stroke={selectedColor.shadow} strokeWidth="5" />

        {/* Cheeks blush */}
        <circle cx="50" cy="130" r="10" fill="#F43F5E" fillOpacity="0.3" />
        <circle cx="150" cy="130" r="10" fill="#F43F5E" fillOpacity="0.3" />

        {/* Snout Area */}
        <ellipse cx="100" cy="128" rx="20" ry="12" fill="#FFFFFF" fillOpacity="0.85" />

        {/* Whiskers */}
        {/* Left whiskers */}
        <line x1="45" y1="125" x2="10" y2="120" stroke="#475569" strokeWidth="3" strokeLinecap="round" />
        <line x1="42" y1="135" x2="8" y2="138" stroke="#475569" strokeWidth="3" strokeLinecap="round" />
        {/* Right whiskers */}
        <line x1="155" y1="125" x2="190" y2="120" stroke="#475569" strokeWidth="3" strokeLinecap="round" />
        <line x1="158" y1="135" x2="192" y2="138" stroke="#475569" strokeWidth="3" strokeLinecap="round" />

        {/* Mouth and Nose */}
        <path
          d="M 96 126 Q 100 122 104 126"
          stroke="#1E293B"
          strokeWidth="3.5"
          strokeLinecap="round"
        />
        {expression === 'sleepy' ? (
          // Sleepy tiny frown
          <path
            d="M 94 133 Q 100 130 106 133"
            stroke="#1E293B"
            strokeWidth="3"
            strokeLinecap="round"
            fill="none"
          />
        ) : expression === 'excited' ? (
          // Excited open mouth
          <path
            d="M 94 131 Q 100 142 106 131 Z"
            fill="#F43F5E"
            stroke="#1E293B"
            strokeWidth="3"
            strokeLinejoin="round"
          />
        ) : (
          // Standard cute cat smile
          <path
            d="M 92 131 Q 100 137 100 131 Q 100 137 108 131"
            stroke="#1E293B"
            strokeWidth="3"
            strokeLinecap="round"
            fill="none"
          />
        )}
        {/* Nose */}
        <polygon points="96,121 104,121 100,126" fill="#F43F5E" />

        {/* Eyes based on expression */}
        {expression === 'happy' && (
          <>
            {/* Curved happy closed eyes */}
            <path d="M 55 110 Q 67 98 75 110" stroke="#1E293B" strokeWidth="4.5" strokeLinecap="round" fill="none" />
            <path d="M 125 110 Q 133 98 145 110" stroke="#1E293B" strokeWidth="4.5" strokeLinecap="round" fill="none" />
          </>
        )}

        {expression === 'cool' && (
          <>
            {/* Sunglasses! */}
            <path d="M 46 100 H 154" stroke="#1E293B" strokeWidth="4" strokeLinecap="round" />
            <rect x="52" y="96" width="34" height="24" rx="8" fill="#1E293B" stroke="#000000" strokeWidth="2" />
            <rect x="114" y="96" width="34" height="24" rx="8" fill="#1E293B" stroke="#000000" strokeWidth="2" />
            {/* Sunglasses gloss lines */}
            <line x1="58" y1="100" x2="68" y2="112" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" />
            <line x1="120" y1="100" x2="130" y2="112" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" />
          </>
        )}

        {expression === 'wink' && (
          <>
            {/* Left open eye, right closed wink */}
            <circle cx="65" cy="106" r="10" fill="#1E293B" />
            <circle cx="62" cy="102" r="3.5" fill="#FFFFFF" />
            <circle cx="68" cy="109" r="1.5" fill="#FFFFFF" />
            
            <path d="M 125 110 Q 135 98 145 110" stroke="#1E293B" strokeWidth="4.5" strokeLinecap="round" fill="none" />
          </>
        )}

        {expression === 'excited' && (
          <>
            {/* Big starry/glimmering open eyes */}
            <circle cx="65" cy="104" r="11" fill="#1E293B" />
            <circle cx="61" cy="99" r="4.5" fill="#FFFFFF" />
            <circle cx="68" cy="107" r="2.5" fill="#FFFFFF" />

            <circle cx="135" cy="104" r="11" fill="#1E293B" />
            <circle cx="131" cy="99" r="4.5" fill="#FFFFFF" />
            <circle cx="138" cy="107" r="2.5" fill="#FFFFFF" />
          </>
        )}

        {expression === 'sleepy' && (
          <>
            {/* Downward curved relaxed sleepy eyes */}
            <path d="M 55 104 Q 65 114 75 104" stroke="#1E293B" strokeWidth="4" strokeLinecap="round" fill="none" />
            <path d="M 125 104 Q 135 114 145 104" stroke="#1E293B" strokeWidth="4" strokeLinecap="round" fill="none" />
            {/* Tiny ZZZ on top-right */}
            <text x="145" y="60" fill="#3B82F6" fontSize="16" fontWeight="bold" fontFamily="monospace">z</text>
            <text x="155" y="48" fill="#3B82F6" fontSize="20" fontWeight="bold" fontFamily="monospace">Z</text>
          </>
        )}

        {/* Accessory Overlays */}
        {accessory === 'glasses' && (
          <>
            {/* Round specs */}
            <circle cx="65" cy="106" r="20" stroke="#D97706" strokeWidth="4.5" fill="none" />
            <circle cx="135" cy="106" r="20" stroke="#D97706" strokeWidth="4.5" fill="none" />
            <line x1="85" y1="106" x2="115" y2="106" stroke="#D97706" strokeWidth="5.5" />
            {/* Side temples */}
            <path d="M 45 106 Q 35 100 25 106" stroke="#D97706" strokeWidth="4" fill="none" />
            <path d="M 155 106 Q 165 100 175 106" stroke="#D97706" strokeWidth="4" fill="none" />
          </>
        )}

        {accessory === 'hat' && (
          <>
            {/* Cute mini party cone hat */}
            <path d="M 75 50 L 100 10 L 125 50 Z" fill="#EF4444" stroke="#B91C1C" strokeWidth="3" />
            {/* Stripes */}
            <path d="M 83 37 Q 100 30 117 37" stroke="#FBBF24" strokeWidth="4" fill="none" />
            <path d="M 89 27 Q 100 22 111 27" stroke="#3B82F6" strokeWidth="4" fill="none" />
            {/* Pom-pom */}
            <circle cx="100" cy="7" r="7" fill="#FBBF24" />
          </>
        )}

        {accessory === 'ribbon' && (
          <>
            {/* Cute pink bow tie at bottom of face */}
            <path d="M 85 168 L 100 175 L 85 182 Z" fill="#F43F5E" stroke="#BE123C" strokeWidth="2.5" />
            <path d="M 115 168 L 100 175 L 115 182 Z" fill="#F43F5E" stroke="#BE123C" strokeWidth="2.5" />
            <circle cx="100" cy="175" r="5" fill="#FFF" stroke="#BE123C" strokeWidth="2" />
          </>
        )}

        {accessory === 'crown' && (
          <>
            {/* Golden royal crown */}
            <path
              d="M 72 45 L 65 20 L 85 30 L 100 12 L 115 30 L 135 20 L 128 45 Z"
              fill="#FBBF24"
              stroke="#B45309"
              strokeWidth="3.5"
              strokeLinejoin="round"
            />
            {/* Jewels on crown points */}
            <circle cx="65" cy="19" r="3.5" fill="#EF4444" />
            <circle cx="100" cy="11" r="3.5" fill="#3B82F6" />
            <circle cx="135" cy="19" r="3.5" fill="#10B981" />
            <circle cx="100" cy="33" r="4.5" fill="#EC4899" />
          </>
        )}
      </svg>
    </div>
  );
}
